import { useEffect, useMemo, useRef, useState } from "react";
import { IoCheckmark, IoClose, IoSearchOutline } from "react-icons/io5";
import { RiExpandUpDownLine } from "react-icons/ri";

/**
 * Button + searchable popover for a single-value facet.
 * `items` are `{ id, name }`; `value` is the selected item id as a string.
 */
const SearchableSelectDropdown = ({
	label,
	icon: Icon,
	items: providedItems = [],
	value = "",
	onChange,
	fullWidth = false,
	searchPlaceholder,
	searchLabel,
	emptyMessage = "No options yet",
	noMatchMessage = "No options match your search",
}) => {
	const items = useMemo(
		() => (Array.isArray(providedItems) ? providedItems : []),
		[providedItems],
	);
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const containerRef = useRef(null);
	const searchRef = useRef(null);
	const triggerRef = useRef(null);

	useEffect(() => {
		if (!open) {
			setQuery("");
			return undefined;
		}

		searchRef.current?.focus();

		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		const handleKeyDown = (event) => {
			if (event.key === "Escape") { setOpen(false); triggerRef.current?.focus(); }
		};

		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open]);

	const selectedItem = useMemo(
		() => items.find((item) => item.id?.toString() === value?.toString()),
		[items, value],
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return items;
		return items.filter((item) => item.name?.toLowerCase().includes(q));
	}, [items, query]);

	const hasSelection = Boolean(value);
	const lowerLabel = label.toLowerCase();

	const pick = (id) => {
		const next = id?.toString() === value?.toString() ? "" : id?.toString() || "";
		onChange(next);
		setOpen(false);
		triggerRef.current?.focus();
	};

	return (
		<div ref={containerRef} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }} className={`catalogue-select relative ${fullWidth ? "w-full" : ""}`}>
			<button
				type="button"
				ref={triggerRef}
				onClick={() => setOpen((prev) => !prev)}
				aria-haspopup="dialog"
				aria-expanded={open}
				className={`h-11 sm:h-10 min-w-[140px] max-w-[220px] inline-flex items-center gap-2 rounded-lg pl-3 pr-2 text-sm border transition-colors duration-200 ${
					fullWidth ? "w-full justify-between" : ""
				} ${
					hasSelection
						? "bg-akgreener bg-opacity-5 border-akgreener text-akgreenest font-medium"
						: "bg-white border-gray-200 text-gray-700 hover:border-akgreen"
				}`}
			>
				<span className="inline-flex min-w-0 items-center gap-2">
					{Icon && (
						<Icon size={16} className={hasSelection ? "text-akgreener" : "text-gray-400"} />
					)}
					<span className="truncate">{selectedItem?.name || label}</span>
				</span>
				<RiExpandUpDownLine className="flex-shrink-0 text-gray-400" size={16} />
			</button>

			{open && (
				<div
					role="dialog"
					aria-label={`Filter by ${lowerLabel}`}
					className={`catalogue-select-popup absolute z-30 mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ${
						fullWidth ? "w-full" : "w-72"
					}`}
				>
					<div className="border-b border-gray-100 p-2">
						<div className="relative">
							<IoSearchOutline
								className="absolute left-2.5 top-1/2 -translate-y-1/2 transform text-gray-400"
								size={15}
							/>
							<input
								ref={searchRef}
								type="search"
								value={query}
								onChange={(event) => setQuery(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter") event.preventDefault();
									if (event.key === "ArrowDown") { event.preventDefault(); containerRef.current?.querySelector('[role="option"]')?.focus(); }
								}}
								placeholder={searchPlaceholder || `Search ${lowerLabel}...`}
								aria-label={searchLabel || `Search ${lowerLabel}`}
								className="h-11 sm:h-9 w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-2 text-sm focus:border-akgreen focus:outline-none focus:ring-1 focus:ring-akgreen"
							/>
						</div>
					</div>

					<ul
						className="max-h-64 overflow-y-auto py-1"
						role="listbox"
						aria-label={label}
						onKeyDown={(event) => {
							const keys = ["ArrowDown", "ArrowUp", "Home", "End"];
							if (!keys.includes(event.key)) return;
							event.preventDefault();
							const options = Array.from(event.currentTarget.querySelectorAll('[role="option"]'));
							const current = options.indexOf(document.activeElement);
							const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : (current + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
							options[next]?.focus();
						}}
					>
						{filtered.length === 0 && (
							<li className="px-3 py-6 text-center text-sm text-gray-500">
								{items.length === 0 ? emptyMessage : noMatchMessage}
							</li>
						)}
						{filtered.map((item) => {
							const id = item.id?.toString();
							const isSelected = id === value?.toString();
							return (
								<li key={item.id}>
									<button
										type="button"
										role="option"
										aria-selected={isSelected}
										onClick={() => pick(item.id)}
										className={`flex w-full min-h-[44px] sm:min-h-0 items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors ${
											isSelected
												? "bg-akgreener bg-opacity-5 font-medium text-akgreenest"
												: "text-gray-700 hover:bg-gray-50"
										}`}
									>
										<span className="flex min-w-0 items-center gap-2">
											<span
												className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
													isSelected
														? "border-akgreener bg-akgreener text-white"
														: "border-gray-300 bg-white"
												}`}
											>
												{isSelected && <IoCheckmark size={12} />}
											</span>
											<span className="truncate">{item.name}</span>
										</span>
									</button>
								</li>
							);
						})}
					</ul>

					{hasSelection && (
						<div className="flex items-center justify-between border-t border-gray-100 p-2">
							<button
								type="button"
								onClick={() => {
									onChange("");
									setOpen(false);
									triggerRef.current?.focus();
								}}
								className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-red-600 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-akgreen"
							>
								<IoClose size={14} />
								Clear {lowerLabel}
							</button>
							<button
								type="button"
								onClick={() => { setOpen(false); triggerRef.current?.focus(); }}
								className="rounded-lg bg-akgreenest px-3 py-1.5 text-xs font-semibold text-white hover:bg-akgreener focus:outline-none focus:ring-2 focus:ring-akgreen focus:ring-offset-1"
							>
								Done
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default SearchableSelectDropdown;
